import { Badge } from '@ordo/ui/badge'
import type { RecipientEntry, SendRecord } from 'types/sends'

/**
 * Shared status pill for a communication send. Extracted so the Messages
 * list, the send-detail view, and the contact comms-history all render the
 * status identically (DRY per apps/boombox/CLAUDE.md).
 */
export function StatusBadge({ status }: { status: SendRecord['status'] }) {
  const variantMap: Record<SendRecord['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
    SENT: 'default',
    PENDING: 'secondary',
    PARTIAL: 'outline',
    FAILED: 'destructive',
  }
  return (
    <Badge variant={variantMap[status]} className="text-xs">
      {status}
    </Badge>
  )
}

/**
 * Locale date/time for a send's `startedAt`. `withSeconds` adds the seconds
 * component (used by the send-detail view where exact ordering matters).
 */
export function formatSendDate(dateStr: string, opts?: { withSeconds?: boolean }): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(opts?.withSeconds ? { second: '2-digit' } : {}),
  })
}

/**
 * Human label for what a send was: the rendered subject if present, else the
 * template name, else a generic "Transactional Send".
 */
export function sendSubjectLabel(send: Pick<SendRecord, 'renderedSubject' | 'template'>): string {
  return send.renderedSubject ?? send.template?.name ?? 'Transactional Send'
}

/**
 * Compact one-line preview of a send's recipients for list views: the first
 * recipient's name (falling back to their address), plus "+N more" when the
 * send went to several. Reads the denormalized `recipients` already on the
 * list payload — no extra fetch.
 */
export function recipientPreview(recipients: RecipientEntry[]): string {
  if (recipients.length === 0) {
    return '—'
  }
  const [first, ...rest] = recipients
  const name = [first.firstName, first.lastName].filter(Boolean).join(' ') || first.address
  return rest.length > 0 ? `${name} +${rest.length} more` : name
}

/**
 * Summary-row variant: list endpoints ship only the first few recipients
 * (`recipientsPreview`), so the "+N more" count must come from the row's
 * total `recipientCount`, not the array length.
 */
export function recipientPreviewFromSummary(preview: RecipientEntry[], recipientCount: number): string {
  const [first] = preview
  if (!first || recipientCount === 0) {
    return '—'
  }
  const name = [first.firstName, first.lastName].filter(Boolean).join(' ') || first.address
  return recipientCount > 1 ? `${name} +${recipientCount - 1} more` : name
}
